import {Box, Button, Paper, Typography, Chip} from "@mui/material";
import {useEffect, useState} from "react";
import {useParams, useNavigate} from "react-router-dom";
import {getNews} from "../services/NewsService";
import {getArticle} from "../services/ArticleService";
import {getBrandingBanner} from "../services/BrandingBannerService";
import {getIntegration} from "../services/IntegrationService";
import {approveFeed, getFeedById, deleteFeed} from "../services/FeedService";
import {useMessageContext} from "../contexts/MessageContext";
import ArticleSummery from "../componenets/ArticleSummery";
import NewsPreview from "../componenets/feed/NewsPreview";
import BannerPreview from "../componenets/feed/BannerPreview";
import ScheduleFeedModal from "../componenets/ScheduleFeedModal";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ScheduleIcon from '@mui/icons-material/Schedule';
import DeleteIcon from '@mui/icons-material/Delete';
import moment from "moment";
import {createOneTimeCalendarEntry, updateOneTimeCalendarEntry} from "../services/CalenderService";

const FeedPage = () => {
  const { feedId } = useParams();
  const navigate = useNavigate();
  const {notificationHandler} = useMessageContext();

  const [feedItem, setFeedItem] = useState(null);
  const [data, setData] = useState(null);
  const [integrations, setIntegrations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(false);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);

  const typeLabels = {
    NEWS: "News",
    WEEKLY_DIGEST: "Weekly Digest",
    NEWS_GENERAL: "General News",
    NEWS_FINANCE: "Finance News",
    SINGLE_POST: "Single Post",
    ARTICLE: "Article",
    BANNER: "Branding Banner",
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING': return 'warning';
      case 'SCHEDULED': return 'info';
      case 'APPROVED': return 'success';
      case 'POSTED': return 'success';
      case 'REJECTED': return 'error';
      default: return 'default';
    }
  };

  useEffect(() => {
    const fetchFeedData = async () => {
      try {
        setLoading(true);

        // Get the feed item first
        const feedResponse = await getFeedById(feedId);
        const feed = feedResponse.data;
        setFeedItem(feed);

        // Get the content data based on feed type
        if (feed.type.includes("NEWS") || feed.type.includes("WEEKLY_DIGEST") ||
            feed.type.includes("NEWS_GENERAL") || feed.type.includes("NEWS_FINANCE")) {
          const newsData = await getNews(feed.entityId);
          setData(newsData.data);
        } else if (feed.type.includes("ARTICLE") || feed.type.includes("SINGLE_POST")) {
          const articleData = await getArticle(feed.entityId);
          setData(articleData.data);
        } else if (feed.type.includes("BANNER")) {
          const bannerData = await getBrandingBanner(feed.entityId);
          setData(bannerData.data);
        }

        // Get integrations if company exists
        if (feed.companyId) {
          const integrationsData = await getIntegration(feed.companyId);
          setIntegrations(integrationsData.data);
        }
      } catch (error) {
        console.error("Error fetching feed data:", error);
        notificationHandler.error("Failed to load feed data");
      } finally {
        setLoading(false);
      }
    };

    if (feedId) {
      fetchFeedData();
    }
  }, [feedId, reload]);

  const handleApprove = async () => {
    try {
      await approveFeed(feedItem.id);
      setReload(prev => !prev);
      notificationHandler.success('Feed approved successfully');
    } catch (error) {
      notificationHandler.error('Failed to approve feed');
    }
  };

  const handleScheduleFeed = async (scheduleData) => {
    try {
      // If feed is already scheduled, update the existing calendar entry
      if (feedItem.calenderId) {
        await updateOneTimeCalendarEntry(feedItem.calenderId, scheduleData);
        notificationHandler.success('Feed rescheduled successfully!');
      } else {
        await createOneTimeCalendarEntry(scheduleData);
        notificationHandler.success('Feed scheduled successfully!');
      }
      setReload(prev => !prev); // Refresh the feed data
    } catch (error) {
      console.error('Error scheduling feed:', error);
      throw error;
    }
  };

  const handleBack = () => {
    navigate('/app/feed');
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this feed?')) {
      deleteFeed(feedItem.id)
        .then(() => {
          notificationHandler.success('Feed deleted');
          navigate('/app/feed');
        })
        .catch(() => notificationHandler.error('Failed to delete feed'));
    }
  };

  const canSchedule = () => {
    return feedItem && feedItem.status !== 'POSTED';
  };

  if (loading) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
          <Typography>Loading...</Typography>
        </Box>
    );
  }

  if (!feedItem) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
          <Typography>Feed not found</Typography>
        </Box>
    );
  }

  return (
      <Box sx={{ padding: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              sx={{ mr: 2 }}
          />

          <Typography variant="h4" component="h1">
            Feed Details
          </Typography>
        </Box>

        {/* Feed Information */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Feed Information
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Type
              </Typography>
              <Typography variant="body1">
                {typeLabels[feedItem.type] || feedItem.type}
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Company
              </Typography>
              <Typography variant="body1">
                {feedItem.companyName || 'N/A'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Status
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                    label={feedItem.status}
                    color={getStatusColor(feedItem.status)}
                    size="small"
                />
                {feedItem.status === 'SCHEDULED' && (
                    <Typography variant="caption" color="text.secondary">
                      (Automatically scheduled)
                    </Typography>
                )}
              </Box>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Manual
              </Typography>
              <Typography variant="body1">
                {feedItem.manual ? 'Yes' : 'No'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Created Date
              </Typography>
              <Typography variant="body1">
                {moment(feedItem.createdDate).format("DD/MM/YY HH:mm:ss")}
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Scheduled Date
              </Typography>
              <Typography variant="body1">
                {feedItem.scheduledDate ? moment.utc(feedItem.scheduledDate).format("DD/MM/YY HH:mm:ss") : "N/A"}
              </Typography>
            </Box>
            {feedItem.formatName && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Format
                </Typography>
                <Typography variant="body1">
                  {feedItem.formatName}
                </Typography>
              </Box>
            )}
            {feedItem.subFormatName && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Sub Format
                </Typography>
                <Typography variant="body1">
                  {feedItem.subFormatName}
                </Typography>
              </Box>
            )}
            {feedItem.title && (
              <Box sx={{ gridColumn: 'span 2' }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Title
                </Typography>
                <Typography variant="body1">
                  {feedItem.title}
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>

        {/* Feed Content */}
        {data && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Content Preview
              </Typography>
              {(feedItem.type.includes("NEWS") || feedItem.type.includes("WEEKLY_DIGEST") ||
                  feedItem.type.includes("NEWS_GENERAL") || feedItem.type.includes("NEWS_FINANCE")) && (
                  <NewsPreview integration={integrations} data={data} item={feedItem} />
              )}
              {(feedItem.type.includes("ARTICLE") || feedItem.type.includes("SINGLE_POST")) && (
                  <ArticleSummery
                      integration={integrations}
                      companyId={feedItem.companyId}
                      setReload={setReload}
                      element={data}
                      isArticle={feedItem.type.includes("ARTICLE")}
                  />
              )}
              {feedItem.type.includes("BANNER") && (
                  <BannerPreview
                      data={data}
                      notificationHandler={notificationHandler}
                      integration={integrations}
                  />
              )}
            </Paper>
        )}

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" onClick={handleBack}>
            Close
          </Button>

          {canSchedule() && (
              <Button
                  variant="outlined"
                  color="info"
                  startIcon={<ScheduleIcon />}
                  onClick={() => setScheduleModalOpen(true)}
              >
                {feedItem.status === 'SCHEDULED' ? 'Reschedule' : 'Schedule for Later'}
              </Button>
          )}

          <Button variant="contained" color="primary" onClick={handleApprove}>
            Approve Feed
          </Button>

          <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
          >
            Delete Feed
          </Button>
        </Box>

        {/* Schedule Modal */}
        <ScheduleFeedModal
            open={scheduleModalOpen}
            onClose={() => setScheduleModalOpen(false)}
            feedItem={feedItem}
            onSchedule={handleScheduleFeed}
        />
      </Box>
  );
};

export default FeedPage;
